import { SrSalesOrderType } from '@plato/data/starrocks/types';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import { DataText } from '../../../components/typography/data-text.tsx';
import { PercentageOrEmpty } from '../../../components/typography/percentage-or-empty.tsx';
import { QuantityText } from '../../../components/typography/quantity-text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { ExtendedSalesOrderItem } from '../../../server/types/grid-expanded-types.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { MaskedContent } from '../../customer-mode/components/masked-content.tsx';
import { LineItemStatusBadge } from '../../statuses/components/line-item-status-badge.tsx';
import { MarginPercentage } from '../components/margin-percentage.tsx';
import { PricePerUnitLabel } from '../components/price-per-unit-label.tsx';
import { ProductLinkCell } from '../components/product-link-cell.tsx';

export type TransactionDetailLineItemsColumnsOptions = {
    type?: SrSalesOrderType;
};

export const useTransactionDetailLineItemsColumns = (options: TransactionDetailLineItemsColumnsOptions) => {
    const { t } = useLocale();
    const { type = SrSalesOrderType.ORDER } = options;
    const { canReadGrossMargin, canReadLineItemRebate, canReadLineItemPurchasePrice } = useAppConfig();

    const transactionDetailLineItemsColumns: ColumnDef<ExtendedSalesOrderItem>[] = useMemo(
        () => [
            {
                accessorKey: 'product_name',
                header: t('table_header__product_name', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <ProductLinkCell
                        erpProductId={row.original.product_variant_id}
                        productName={row.original.product_name}
                    />
                ),
                size: 200,
            },
            {
                accessorKey: 'line_status',
                header: t('table_header__line_status', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => <LineItemStatusBadge status={row.original.status} type={type} />,
            },
            {
                accessorKey: 'quantity',
                header: t('table_header__quantity_ordered', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <QuantityText quantity={row.original.quantity} unit={row.original.quantity_unit_sales} />
                ),
            },
            {
                accessorKey: 'price_per_unit',
                header: t('table_header__price', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <>
                        <DataText>
                            <CurrencyOrEmpty decimals={2}>{row.original.sales_price}</CurrencyOrEmpty>
                        </DataText>
                        <PricePerUnitLabel
                            scaleFactor={row.original.price_scale_factor}
                            units={row.original.quantity_unit_sales}
                            locale={t('locale')}
                        />
                    </>
                ),
            },
            ...(canReadLineItemRebate
                ? ([
                      {
                          accessorKey: 'discount_percent',
                          header: t('table_header__rebate', {
                              ns: 'line-item-table',
                          }),
                          cell: ({ row }) =>
                              typeof row.original.discount_percent === 'number' ? (
                                  <DataText>
                                      <PercentageOrEmpty>
                                          {row.original.discount_percent / 100}
                                      </PercentageOrEmpty>
                                  </DataText>
                              ) : (
                                  '-'
                              ),
                      },
                  ] as ColumnDef<ExtendedSalesOrderItem>[])
                : []),
            {
                accessorKey: 'line_total_money',
                header: t('table_header__total', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.line_total}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            ...(canReadGrossMargin
                ? ([
                      {
                          accessorKey: 'order_line_gross_margin',
                          header: () => (
                              <MaskedContent>
                                  {t('table_header__gross_margin', { ns: 'line-item-table' })}
                              </MaskedContent>
                          ),
                          cell: ({ row }) => (
                              <MaskedContent>
                                  <DataText>
                                      <CurrencyOrEmpty decimals={2}>
                                          {row.original.gross_margin}
                                      </CurrencyOrEmpty>
                                  </DataText>
                              </MaskedContent>
                          ),
                      },
                      {
                          accessorKey: 'line_gross_margin_percentage',
                          header: () => (
                              <MaskedContent>
                                  {t('table_header__gross_margin_percentage', { ns: 'line-item-table' })}
                              </MaskedContent>
                          ),
                          cell: ({ row }) => (
                              <MaskedContent>
                                  <DataText>
                                      <MarginPercentage
                                          margin={row.original.gross_margin}
                                          revenue={row.original.line_total}
                                      />
                                  </DataText>
                              </MaskedContent>
                          ),
                      },
                  ] as ColumnDef<ExtendedSalesOrderItem>[])
                : []),
            ...(canReadLineItemPurchasePrice
                ? ([
                      {
                          accessorKey: 'encrypted_purchase_price',
                          header: t('table_header__encrypted_purchase_price', {
                              ns: 'line-item-table',
                          }),
                          cell: ({ row }: { row: { original: ExtendedSalesOrderItem } }) =>
                              row.original.encrypted_purchase_price,
                      },
                  ] as ColumnDef<ExtendedSalesOrderItem>[])
                : []),
        ],
        [canReadGrossMargin, canReadLineItemPurchasePrice, canReadLineItemRebate, t, type],
    );

    return transactionDetailLineItemsColumns;
};
